import { Button, Col, Form, Modal, Row, FormSelect } from "react-bootstrap";
import { Formik } from "formik";
import { PublicTransportImportType } from "../../../../constant/PublicTransportImportType";
import { triggerImportSchema } from "./schema";
import { ErrorText } from "../../../../@ui/ErrorText";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { DateTime } from "luxon";
import ThemedModal from "../../../../@ui/Modal/ThemedModal";
import LoadingSpinner from "../../../Loading/LoadingSpinner";
import { usePublicTransportService } from "../../../../hooks";
import { SnapshotWindow } from "../../../../types/LayerAdmin/SnapshotWindow";
import AtocImportFields from "./components/AtocImportFields";
import GtfsImportFields from "./components/GtfsImportFields";
import NcsdImportFields from "./components/NcsdImportFields";
import TranslinkImportFields from "./components/TranslinkImportFields";
import UsTransitMergeImportFields from "./components/UsTransitMergeImportFields";
import {PublicTransportImport} from "../../../../types/LayerAdmin/PublicTransportImport";

interface TriggerImportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (
	type: PublicTransportImportType,
	file?: File | null,
	quarter?: string,
	description?: string,
	additionalFiles?: {
	  busArchive?: File | null;
	  railArchive?: File | null;
	  metroArchive?: File | null;
	},
	usTransitImportId?: string,
	transitLandImportId?: string
  ) => void;
  isSubmitting: boolean;
  hasErrored: boolean;
}

interface TriggerForm {
  type?: PublicTransportImportType;
  atocFile?: File | null;
  gtfsFile?: File | null;
  quarter?: string;
  gtfsName?: string;
  ncsdFile?: File | null;
  busArchive?: File | null;
  railArchive?: File | null;
  metroArchive?: File | null;
  usTransitImportId?: string;
  transitLandImportId?: string;
}

const StyledModalTitle = styled(Modal.Title)`
  font-size: 1.25rem;
`;

const TriggerImportModal = ({
  show,
  onClose,
  onSubmit,
  isSubmitting,
  hasErrored,
}: TriggerImportModalProps) => {
  const publicTransportService = usePublicTransportService();
  const [snapshowWindows, setSnapshotWindows] = useState<SnapshotWindow[]>([]);
	const [quarterImports, setQuarterImports] = useState<PublicTransportImport[]>([]);

  useEffect(() => {
	publicTransportService
	  .GetSnapshotWindow()
	  .then((data) => {
		setSnapshotWindows(data);
	  })
	  .catch((_) => {});
  }, [publicTransportService]);

	const currentQuarter = `${DateTime.now().year} Q${DateTime.now().quarter}`;

	useEffect((() =>{
		publicTransportService
			.GetImports(currentQuarter)
			.then((data) => {
				setQuarterImports(data);
			})
			.catch((_) => {});
	}), [publicTransportService, currentQuarter])

  const submit = (values: TriggerForm) => {
	const type = Number(values.type) as PublicTransportImportType;
	const {
	  atocFile,
	  gtfsFile,
	  quarter,
	  gtfsName,
	  ncsdFile,
	  busArchive,
	  railArchive,
	  metroArchive,
	  usTransitImportId,
	  transitLandImportId,
	} = values;
	if (type === PublicTransportImportType.Gtfs) {
	  onSubmit(type!, gtfsFile, quarter, gtfsName);
	} else if (type === PublicTransportImportType.AtocCif) {
	  onSubmit(type!, atocFile, undefined, undefined);
	} else if (type === PublicTransportImportType.NCSD) {
	  onSubmit(type!, ncsdFile, undefined, undefined);
	} else if (type === PublicTransportImportType.Translink) {
	  onSubmit(type!, null, undefined, undefined, {
		busArchive,
		railArchive,
		metroArchive,
	  });
	} else if (type === PublicTransportImportType.UsTransitMerge) {
	  onSubmit(
		type!,
		null,
		undefined,
		undefined,
		undefined,
		usTransitImportId,
		transitLandImportId
	  );
	} else {
	  onSubmit(type!, null, undefined, undefined);
	}
  };

  const [atocTouched, setAtocTouched] = useState(false);
  const [gtfsTouched, setGtfsTouched] = useState(false);
  const [ncsdTouched, setNcsdTouched] = useState(false);
  const [busArchiveTouched, setBusArchiveTouched] = useState(false);
  const [railArchiveTouched, setRailArchiveTouched] = useState(false);
  const [metroArchiveTouched, setMetroArchiveTouched] = useState(false);

  const initialValues: TriggerForm = {
    type: undefined,
    atocFile: null,
    gtfsFile: null,
    quarter: "",
    gtfsName: "",
    ncsdFile: null,
    busArchive: null,
    railArchive: null,
    metroArchive: null,
    usTransitImportId: "",
    transitLandImportId: "",
  };

  const options = [
	{
	  value: PublicTransportImportType.AtocCif,
	  label: "ATOC",
	},
	{
	  value: PublicTransportImportType.Traveline,
	  label: "Traveline",
	},
	{
	  value: PublicTransportImportType.TransitLand,
	  label: "Transitland",
	},
	{
	  value: PublicTransportImportType.Gtfs,
	  label: "One Off GTFS",
	},
	{
	  value: PublicTransportImportType.NCSD,
	  label: "NCSD",
	},
	{
	  value: PublicTransportImportType.Translink,
	  label: "Translink",
	},
	{
	  value: PublicTransportImportType.BoundaryVectorTiles,
	  label: "Boundary Vector Tiles",
	},
    {
      value: PublicTransportImportType.UsTransit,
      label: "US Transit"
    },
    {
      value: PublicTransportImportType.UsTransitMerge,
      label: "US Transit Merge"
    }
  ];

  return (
	<ThemedModal centered show={show} onHide={onClose}>
	  <Formik
		onSubmit={submit}
		initialValues={initialValues}
		validationSchema={triggerImportSchema}
	  >
		{({
		  values,
		  handleSubmit,
		  handleChange,
		  setFieldValue,
		  touched,
		  errors,
		  handleBlur,
		}) => (
		  <Form onSubmit={handleSubmit}>
			<Modal.Header className="bg-secondary text-white" closeButton>
			  <StyledModalTitle>Select imports to trigger</StyledModalTitle>
			</Modal.Header>
			<Modal.Body>
			  <Row className="justify-content-center">
				<Col className="col-md-10">
				  <FormSelect
					className="w-100 mb-3"
					name="type"
					onChange={handleChange}
					value={values.type}
					onBlur={handleBlur}
				  >
					<option value="">Select...</option>
					{options.map((option) => (
					  <option key={option.value} value={option.value}>
						{option.label}
					  </option>
					))}
				  </FormSelect>
				  {errors.type && touched.type && (
					<ErrorText className="text-danger">
					  Type is required
					</ErrorText>
				  )}
				</Col>
			  </Row>
			  {values.type?.toString() ===
				PublicTransportImportType.AtocCif.toString() && (
				<AtocImportFields
				  setFieldValue={setFieldValue}
				  errors={errors}
				  atocTouched={atocTouched}
				  setAtocTouched={setAtocTouched}
				/>
			  )}
			  {values.type?.toString() ===
				PublicTransportImportType.Gtfs.toString() && (
				<GtfsImportFields
				  values={values}
				  handleChange={handleChange}
				  handleBlur={handleBlur}
				  setFieldValue={setFieldValue}
				  errors={errors}
				  gtfsTouched={gtfsTouched}
				  setGtfsTouched={setGtfsTouched}
				  snapshotWindows={snapshowWindows}
				/>
			  )}
			  {values.type?.toString() ===
				PublicTransportImportType.NCSD.toString() && (
				<NcsdImportFields
				  setFieldValue={setFieldValue}
				  errors={errors}
				  ncsdTouched={ncsdTouched}
				  setNcsdTouched={setNcsdTouched}
				/>
			  )}
			  {values.type?.toString() ===
				PublicTransportImportType.Translink.toString() && (
				<TranslinkImportFields
				  setFieldValue={setFieldValue}
				  errors={errors}
				  busArchiveTouched={busArchiveTouched}
				  setBusArchiveTouched={setBusArchiveTouched}
				  railArchiveTouched={railArchiveTouched}
				  setRailArchiveTouched={setRailArchiveTouched}
				  metroArchiveTouched={metroArchiveTouched}
				  setMetroArchiveTouched={setMetroArchiveTouched}
				/>
			  )}
			  {values.type?.toString() ===
				PublicTransportImportType.UsTransitMerge.toString() && (
				<UsTransitMergeImportFields
				  values={values}
				  handleChange={handleChange}
				  handleBlur={handleBlur}
				  errors={errors}
				  touched={touched}
					imports={quarterImports}
				/>
			  )}
			</Modal.Body>
			<Modal.Footer>
			  {!hasErrored && (
				<>
				  {isSubmitting && (
					<span className="text-warning">
					  This might take a while, <i>don't refresh</i>
					</span>
				  )}
				  <Button
					disabled={isSubmitting}
					onClick={() => {}}
					type="submit"
					variant="primary"
				  >
					{!isSubmitting && <>Trigger Import</>}
					{isSubmitting && (
					  <>
						<LoadingSpinner textColor="text-light" />
					  </>
					)}
				  </Button>
				</>
			  )}
			  {hasErrored && (
				<>
				  <span className="text-danger">
					These has been an error attempting to start the import!
				  </span>
				</>
			  )}
			</Modal.Footer>
		  </Form>
		)}
	  </Formik>
	</ThemedModal>
  );
};

export default TriggerImportModal;
